import { ApiError } from '../api/problem'
import type { TenantIzvor } from '../api/types'

/**
 * Odakle gostu tenant. `/auth/*` je jedina površina na kojoj tenant dolazi iz
 * tijela zahtjeva (CLAUDE.md §4), i to iz TOČNO JEDNOG izvora:
 *
 * 1. `qr_token` — gost je skenirao naljepnicu stanice (`/g?s=<token>`). To je
 *    normalan ulaz i usput govori GDJE gost stoji, pa mu se polazište može
 *    predodabrati;
 * 2. `subdomena` — gost je otvorio adresu resorta (`punta-grande.getshuttly.com`).
 *
 * Nema li ni jednog, korisnik upisuje subdomenu rukom: to je ekran za dev
 * (`localhost`) i za goste koji su aplikaciju instalirali s tuđe adrese, a ne
 * pretpostavka koju bi klijent smio izmisliti — kriv tenant je tuđi resort.
 */

/** Naziv parametra u ulaznoj adresi: `/g?s=<qr_token>`. */
export const QR_PARAMETAR = 's'

/**
 * Registracijski QR (pult, sobni letak): `/g?r=<registracija_token>`. Drugi
 * parametar, jer je i token drugog tipa — naljepnica sa stajališta kaže GDJE
 * gost stoji, ovaj kod samo koji je resort i da se otvara obrazac gosta hotela.
 * Mijeranje u isti parametar značilo bi da naljepnica s plaže traži sobu.
 */
export const REG_PARAMETAR = 'r'

/** Veza iz dobrodošlice: `/g/ulaz?t=<tajna>`. */
export const VEZA_PARAMETAR = 't'

/**
 * Hostovi na kojima subdomena nije adresa resorta nego okruženje ili platforma.
 * `dev` je u popisu jer je `dev.getshuttly.com` cijelo razvojno okruženje: bez
 * toga je gost na golom `/g` dobivao "Resort ne postoji" za resort koji nikad
 * nije ni postojao (zadatak 10 §5).
 */
const NIJE_RESORT = new Set(['www', 'app', 'localhost', 'sys', 'dev'])

/**
 * Zadnji POTVRĐEN resort. Gost koji je aplikaciju instalirao s naljepnice
 * otvara je poslije s ikone, bez `?s=` u adresi — i bez ovoga bi svaki put
 * ispočetka upisivao subdomenu.
 *
 * CLAUDE.md §13 nabraja što smije u `localStorage` (refresh token i jezik) i
 * ovo je treća stavka; upisana je namjerno i po zadatku 10 §5. Subdomena nije
 * ni tajna ni osobni podatak nego ADRESA — ista ona koja bi kod pravog resorta
 * stajala u hostu. Pamti se tek kad je poslužitelj potvrdi (vidi
 * `zapamtiResort`), da izmišljen upis ne bi tiho postao zatečeni resort.
 */
const KLJUC_RESORTA = 'shuttly.gost.resort'

export function zapamcenResort(): string | null {
  try {
    const zapamceno = globalThis.localStorage?.getItem(KLJUC_RESORTA)?.trim()
    return zapamceno ? zapamceno : null
  } catch {
    return null
  }
}

/** Zove se tek nakon odgovora koji dokazuje da resort postoji (bilo koji `/auth/*` osim 404). */
export function zapamtiResort(izvor: TenantIzvor): void {
  if (!('subdomena' in izvor) || !izvor.subdomena) return

  try {
    globalThis.localStorage?.setItem(KLJUC_RESORTA, izvor.subdomena)
  } catch {
    /* Bez pohrane gost subdomenu upisuje pri svakom dolasku — neugodno, ne fatalno. */
  }
}

/**
 * Je li greška "tog resorta nema". Isti ključ vraća i nepoznata subdomena i
 * nepoznat `qr_token` (naljepnica tuđeg resorta), a gostu je odgovor isti:
 * pitanje odakle je došao, ne poruka o resortu.
 */
export function jeNepoznatResort(kvar: unknown): boolean {
  return kvar instanceof ApiError && kvar.key === 'tenant_not_found'
}

/** Resort kojeg poslužitelj ne poznaje ne smije ostati zapamćen. */
export function zaboraviResort(): void {
  try {
    globalThis.localStorage?.removeItem(KLJUC_RESORTA)
  } catch {
    /* Nema se što zaboraviti. */
  }
}

export function qrIzAdrese(search: string): string | null {
  return parametar(search, QR_PARAMETAR)
}

/** Token registracijskog QR-a iz adrese; `null` kad gost nije došao s letka. */
export function regIzAdrese(search: string): string | null {
  return parametar(search, REG_PARAMETAR)
}

/** Tajna veze iz dobrodošlice. */
export function vezaIzAdrese(search: string): string | null {
  return parametar(search, VEZA_PARAMETAR)
}

function parametar(search: string, ime: string): string | null {
  const vrijednost = new URLSearchParams(search).get(ime)?.trim()

  return vrijednost ? vrijednost : null
}

/**
 * Subdomena iz hosta: `punta-grande.getshuttly.com` → `punta-grande`.
 * `localhost`, IP adresa i goli `getshuttly.com` nemaju resort u imenu.
 */
export function subdomenaIzHosta(hostname: string): string | null {
  const dijelovi = hostname.toLowerCase().split('.')

  if (dijelovi.length < 3) return null
  if (/^\d+$/.test(dijelovi[dijelovi.length - 1] ?? '')) return null

  const prvi = dijelovi[0] ?? ''

  return prvi.length >= 3 && !NIJE_RESORT.has(prvi) ? prvi : null
}

/**
 * Izvor tenanta za `/auth/*`, ili `null` ako ga treba upisati korisnik.
 *
 * Redoslijed je od najkonkretnijeg prema najslabijem:
 * 1. **QR** — naljepnica je konkretnija od adrese (gost s instalirane
 *    aplikacije jednog resorta može skenirati kod drugog);
 * 2. **host** — `punta-grande.getshuttly.com` govori sam za sebe;
 * 3. **zapamćeno** — resort iz kojeg je gost zadnji put ušao i koji je
 *    poslužitelj potvrdio; jedini razlog zašto instalirana aplikacija bez
 *    `?s=` ne pita ispočetka.
 *
 * `null` NIJE greška nego pitanje: "s koje si naljepnice došao". Nikad se ne
 * pogađa — kriva subdomena je tuđi resort (CLAUDE.md §4).
 */
export function razrijesiTenant(search: string, hostname: string): TenantIzvor | null {
  const qr = qrIzAdrese(search)
  if (qr) return { qr_token: qr }

  const subdomena = subdomenaIzHosta(hostname) ?? zapamcenResort()

  return subdomena ? { subdomena } : null
}
